import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import {
  clearOrderToasts,
  dismissToast,
  setExpanded
} from '@/store/notifications';
import { openCallModal } from '@/store/calls';
import { type ICallPayload } from '@/types/ICallPayload';
import { isOrderNotification } from '@/utils/isOrderNotification';
import { isCallNotification } from '@/utils/isCallNotification';
import { OrderToastCard } from './components/OrderToastCard/OrderToastCard';
import { CallToastCard } from './components/CallToastCard/CallToastCard';
import { CollapsedOrdersToast } from './components/CollapsedOrdersToast/CollapsedOrdersToast';
import styles from './orderToastStack.module.scss';

const COLLAPSE_THRESHOLD = 2;

/**
 * С какого числа карточек звонков группа становится скроллящейся.
 * Скролл — страховка от ухода стека за нижний край экрана, но контейнер с
 * `overflow` обрезает мягкое свечение карточек по своим границам, из-за чего
 * низ последней карточки выглядит срезанным. Пока звонков мало и до края далеко,
 * скролл не нужен — включаем его только когда карточки действительно копятся.
 */
const CALLS_SCROLL_THRESHOLD = 4;

/**
 * Общий контейнер тостов. Подписан на эфемерный слой тостов слайса
 * notifications и делит очередь по типу уведомления:
 * - звонки — всегда отдельными карточками, без схлопывания;
 * - заказы — до 2 уведомлений отдельными карточками, больше 2 — один
 *   схлопнутый тост со сводкой и раскрытием по клику.
 */
export const OrderToastStack: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queue = useAppSelector((state) => state.notifications.toasts);
  const isExpanded = useAppSelector((state) => state.notifications.isExpanded);

  const handleNavigate = useCallback(
    (path: string) => {
      void navigate(path);
    },
    [navigate]
  );

  const handleClose = useCallback(
    (id: string) => {
      dispatch(dismissToast(id));
    },
    [dispatch]
  );

  const handleCloseAll = useCallback(() => {
    dispatch(clearOrderToasts());
  }, [dispatch]);

  const handleToggle = useCallback(
    (expanded: boolean) => {
      dispatch(setExpanded(expanded));
    },
    [dispatch]
  );

  const handleOpenCall = useCallback(
    (payload: ICallPayload) => {
      dispatch(openCallModal(payload));
    },
    [dispatch]
  );

  const callToasts = queue.filter(isCallNotification);
  const orderToasts = queue.filter(isOrderNotification);

  if (callToasts.length === 0 && orderToasts.length === 0) {
    return null;
  }

  const isCollapsedView = orderToasts.length > COLLAPSE_THRESHOLD;

  return (
    <div
      className={styles['order-toast-stack']}
      role="status"
      aria-live="polite"
    >
      {callToasts.length > 0 && (
        <div
          className={cn(styles['order-toast-stack__group'], {
            [styles['order-toast-stack__group_scrollable']]:
              callToasts.length > CALLS_SCROLL_THRESHOLD
          })}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {callToasts.map((notification) => (
              <CallToastCard
                key={notification.id}
                notification={notification}
                onOpen={handleOpenCall}
                onClose={handleClose}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {orderToasts.length > 0 && (
        <div className={styles['order-toast-stack__group']}>
          <AnimatePresence mode="popLayout" initial={false}>
            {isCollapsedView ? (
              <CollapsedOrdersToast
                key="collapsed"
                notifications={orderToasts}
                isExpanded={isExpanded}
                onToggle={handleToggle}
                onNavigate={handleNavigate}
                onClose={handleClose}
                onCloseAll={handleCloseAll}
              />
            ) : (
              orderToasts.map((notification) => (
                <OrderToastCard
                  key={notification.id}
                  notification={notification}
                  onNavigate={handleNavigate}
                  onClose={handleClose}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
